import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  Renderer2,
  ChangeDetectorRef,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { ScriptService } from 'src/app/services/script.service';
import { RuntimeEnv, RuntimeEnvService } from 'src/app/services/runtime-env.service';

declare const Epago: any;
declare const sha512: any;

const PAYMENT_METHODS = [
  { id: 'efectivo', label: 'Pago Efectivo / Transferencia bancaria', hasSdkForm: false },
  { id: 'cards', label: 'Tarjetas Interbank', hasSdkForm: true },
  { id: 'agora', label: 'App Agora', hasSdkForm: true },
];

const BUTTON_STYLES = [
  { id: 'green', label: 'Verde' },
  { id: 'blue', label: 'Azul' },
  { id: 'red', label: 'Rojo' },
  { id: 'outline', label: 'Outline' },
  { id: 'dark', label: 'Oscuro' },
];

const CREDENTIALS_STORAGE_KEY = 'estilos-inyectables.credentials';
const SDK_HOST_STORAGE_KEY = 'estilos-inyectables.sdkHost';
const DEFAULT_API_KEY = '3247914d-2e33-4193-af72-ce6f01c6d4e4';
const DEFAULT_SECRET = '343db61fbe5f438b8c16ca86f59ce18f';

type SdkHostId = 'QAS' | 'DEV' | 'REACT_SDK_DEV';
type HostCredentials = { apiKey: string; secret: string };

const SDK_HOST_IDS: SdkHostId[] = ['QAS', 'DEV', 'REACT_SDK_DEV'];

function defaultCredentialsMap(): Record<SdkHostId, HostCredentials> {
  return {
    QAS: { apiKey: DEFAULT_API_KEY, secret: DEFAULT_SECRET },
    DEV: { apiKey: DEFAULT_API_KEY, secret: DEFAULT_SECRET },
    REACT_SDK_DEV: { apiKey: DEFAULT_API_KEY, secret: DEFAULT_SECRET },
  };
}

function readStoredCredentials(): Record<SdkHostId, HostCredentials> {
  const defaults = defaultCredentialsMap();
  try {
    const raw = localStorage.getItem(CREDENTIALS_STORAGE_KEY);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw) as Partial<Record<SdkHostId, Partial<HostCredentials>>>;
    SDK_HOST_IDS.forEach((host) => {
      const apiKey = parsed?.[host]?.apiKey?.trim();
      const secret = parsed?.[host]?.secret?.trim();
      if (apiKey) defaults[host].apiKey = apiKey;
      if (secret) defaults[host].secret = secret;
    });
    if (!parsed?.REACT_SDK_DEV?.apiKey) defaults.REACT_SDK_DEV.apiKey = defaults.DEV.apiKey;
    if (!parsed?.REACT_SDK_DEV?.secret) defaults.REACT_SDK_DEV.secret = defaults.DEV.secret;
  } catch {
    /* ignore */
  }
  return defaults;
}

function readStoredSdkHost(): SdkHostId | null {
  try {
    const raw = localStorage.getItem(SDK_HOST_STORAGE_KEY);
    if (raw === 'QAS' || raw === 'DEV' || raw === 'REACT_SDK_DEV') return raw;
  } catch {
    /* ignore */
  }
  return null;
}

const SDK_HOSTS = [
  {
    id: 'QAS' as const,
    label: 'qas.pay.button',
    scriptKey: 'QAS_PA',
    environment: 'quality' as const,
    url: 'https://qas.pay.button.epago.pe/web/pa-1.0.0/sdk.js',
  },
  {
    id: 'DEV' as const,
    label: 'dev.pay.button',
    scriptKey: 'DEV',
    environment: 'development' as const,
    url: 'https://dev.pay.button.epago.pe/web/1.0.0/sdk.js',
  },
  {
    id: 'REACT_SDK_DEV' as const,
    label: 'DEV REACT SDK',
    scriptKey: 'REACT_SDK_DEV',
    environment: 'development' as const,
    url: 'https://dev.pay.button.epago.pe/web/react-sdk-1.0.0/sdk.js',
  },
];

const TYPOGRAPHY_OPTIONS = [
  { id: 'default', label: 'Predeterminada' },
  { id: 'Inter', label: 'Inter' },
  { id: 'Roboto', label: 'Roboto' },
  { id: 'Open Sans', label: 'Open Sans' },
  { id: 'Lato', label: 'Lato' },
  { id: 'Poppins', label: 'Poppins' },
  { id: 'Montserrat', label: 'Montserrat' },
  { id: 'Source Sans 3', label: 'Source Sans 3' },
  { id: 'Oswald', label: 'Oswald' },
  { id: 'Raleway', label: 'Raleway' },
  { id: 'Nunito', label: 'Nunito' },
  { id: 'Work Sans', label: 'Work Sans' },
];

const DEFAULT_ITEMS = [
  {
    name: 'Heladera',
    quantity: 1,
    unitAmount: 10,
    amount: 10,
    sku: '1020304005060',
    category: 'Electrodoméstico',
    discount: 0,
    shipping: 0,
  },
];

@Component({ standalone: false,
  selector: 'app-estilos-inyectables',
  templateUrl: './estilos-inyectables.component.html',
  styleUrls: ['./estilos-inyectables.component.css'],
})
export class EstilosInyectablesComponent implements OnInit, OnDestroy {
  @ViewChild('epagoContainer', { static: false }) epagoContainer: ElementRef<HTMLDivElement> | null = null;

  readonly paymentMethods = PAYMENT_METHODS;
  readonly buttonStyles = BUTTON_STYLES;
  readonly typographyOptions = TYPOGRAPHY_OPTIONS;
  readonly sdkHosts = SDK_HOSTS;

  selected: string | null = null;
  formReady = false;
  result: 'success' | 'error' | null = null;
  resultData: any = null;
  payResult: {
    message: string;
    processorMessage: string;
    orderNumber: string;
    amountLabel: string;
    referenceNumber: string;
  } | null = null;
  buttonStyle = 'green';
  typography = 'default';
  sdkHost: SdkHostId = 'QAS';
  apiKey = DEFAULT_API_KEY;
  secret = DEFAULT_SECRET;

  items = [...DEFAULT_ITEMS];
  discounts = 0;
  shipping = 0;
  fee = 0;
  currency = 'PEN';
  customer = {
    identifier: 'a2324f1a-d527-4e3a-bcf4-aea3125f5f93',
    name: 'Jhony',
    lastName: 'Pacheco',
    email: 'test@test.com',
    phone: '998910021',
    documentNumber: '98910021',
    address1: 'Av Zeta 1',
  };

  private formRef: any = null;
  private scriptLoaded = false;
  private scriptLoadPromise: Promise<void> | null = null;
  private loadedScriptEl: HTMLScriptElement | null = null;
  private loadedScriptKey: string | null = null;
  private envSub?: Subscription;
  private formGeneration = 0;
  private credentialsByHost = readStoredCredentials();

  constructor(
    private scriptService: ScriptService,
    private runtimeEnv: RuntimeEnvService,
    private renderer: Renderer2,
    private cdr: ChangeDetectorRef
  ) {}

  get currentSdkHost(): (typeof SDK_HOSTS)[number] {
    return this.sdkHosts.find((h) => h.id === this.sdkHost) ?? this.sdkHosts[0];
  }

  get amount(): number {
    return this.calculateTotal(this.items, this.discounts, this.shipping, this.fee);
  }

  ngOnInit(): void {
    const storedHost = readStoredSdkHost();
    if (storedHost) {
      this.sdkHost = storedHost;
      const expectedEnv = this.runtimeEnvForHost(storedHost);
      if (this.runtimeEnv.get() !== expectedEnv) {
        this.runtimeEnv.set(expectedEnv);
      }
    } else {
      this.applyRuntimeEnv(this.runtimeEnv.get(), false);
    }
    this.applyHostCredentials();
    this.envSub = this.runtimeEnv.env$.subscribe((env) => this.applyRuntimeEnv(env, true));
    this.loadSdk();
    this.loadGoogleFont();
  }

  ngOnDestroy(): void {
    this.envSub?.unsubscribe();
    this.destroyForm();
  }

  onSdkHostChange(): void {
    this.persistSdkHost();
    this.runtimeEnv.set(this.runtimeEnvForHost(this.sdkHost));
    this.applyHostCredentials();
    this.reloadSdkAndForm();
  }

  onCredentialsInput(): void {
    this.persistCurrentCredentials();
  }

  onCredentialsChange(): void {
    this.persistCurrentCredentials();
    this.remountFormIfNeeded();
  }

  private persistCurrentCredentials(): void {
    this.credentialsByHost[this.sdkHost] = {
      apiKey: this.apiKey.trim(),
      secret: this.secret.trim(),
    };
    try {
      localStorage.setItem(CREDENTIALS_STORAGE_KEY, JSON.stringify(this.credentialsByHost));
    } catch {
      /* ignore */
    }
  }

  private applyHostCredentials(): void {
    const stored = this.credentialsByHost[this.sdkHost];
    this.apiKey = stored?.apiKey || DEFAULT_API_KEY;
    this.secret = stored?.secret || DEFAULT_SECRET;
  }

  private remountFormIfNeeded(): void {
    if (this.selected !== 'cards' && this.selected !== 'agora') return;
    this.destroyForm();
    this.cdr.detectChanges();
    setTimeout(() => this.initForm(), 50);
  }

  private runtimeEnvForHost(host: SdkHostId): RuntimeEnv {
    return host === 'QAS' ? 'QA' : 'DEV';
  }

  private persistSdkHost(): void {
    try {
      localStorage.setItem(SDK_HOST_STORAGE_KEY, this.sdkHost);
    } catch {
      /* ignore */
    }
  }

  private applyRuntimeEnv(env: RuntimeEnv, reloadIfChanged: boolean): void {
    if (this.sdkHost === 'REACT_SDK_DEV' && env === 'DEV') return;
    const nextHost: SdkHostId = env === 'DEV' ? 'DEV' : 'QAS';
    if (nextHost === this.sdkHost) return;
    this.sdkHost = nextHost;
    this.persistSdkHost();
    this.applyHostCredentials();
    this.cdr.detectChanges();
    if (reloadIfChanged) {
      this.reloadSdkAndForm();
    }
  }

  private reloadSdkAndForm(): void {
    const selected = this.selected;
    this.result = null;
    this.resultData = null;
    this.unloadSdk();
    if (typeof (window as any).Epago !== 'undefined') {
      window.location.reload();
      return;
    }
    this.loadSdk().then(() => {
      if (selected === 'cards' || selected === 'agora') {
        this.selected = selected;
        this.cdr.detectChanges();
        this.initForm();
      }
    });
  }

  loadSdk(): Promise<void> {
    const key = this.currentSdkHost.scriptKey;
    if (this.scriptLoaded && this.loadedScriptKey === key && typeof (window as any).Epago !== 'undefined') {
      return this.scriptLoadPromise ?? Promise.resolve();
    }
    if (this.scriptLoadPromise && this.loadedScriptKey === key) {
      return this.scriptLoadPromise;
    }
    this.unloadSdk();
    this.loadedScriptKey = key;
    this.scriptLoadPromise = new Promise((resolve) => {
      const script = this.scriptService.loadJsScript(this.renderer, key);
      script.id = 'epago-script-estilos-inyectables';
      this.loadedScriptEl = script;
      script.onload = () => {
        this.scriptLoaded = true;
        resolve();
      };
      script.onerror = () => {
        console.error('Error cargando SDK:', this.currentSdkHost.url);
        resolve();
      };
    });
    return this.scriptLoadPromise;
  }

  private unloadSdk(): void {
    this.destroyForm();
    if (this.loadedScriptEl?.parentNode) {
      this.loadedScriptEl.parentNode.removeChild(this.loadedScriptEl);
    }
    this.loadedScriptEl = null;
    this.loadedScriptKey = null;
    this.scriptLoaded = false;
    this.scriptLoadPromise = null;
    try {
      delete (window as any).Epago;
    } catch {
      (window as any).Epago = undefined;
    }
  }

  loadGoogleFont(): void {
    if (this.typography === 'default') return;
    const id = 'epago-demo-google-font';
    let link = document.getElementById(id) as HTMLLinkElement | null;
    const family = encodeURIComponent(this.typography);
    const href = `https://fonts.googleapis.com/css2?family=${family}:wght@400;500;600;700&display=swap`;
    if (!link) {
      link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
    link.href = href;
  }

  calculateTotal(
    items: typeof DEFAULT_ITEMS,
    discounts: number,
    shipping: number,
    fee: number
  ): number {
    let total = items.reduce((acc, item) => {
      const discountNeg = -(Math.sign(item.discount) === 1 ? item.discount : -item.discount);
      return acc + (item.amount + discountNeg + item.shipping);
    }, 0);
    const discountVal = -(Math.sign(discounts) === 1 ? discounts : -discounts);
    return total + discountVal + shipping + fee;
  }

  generateOrderId(): string {
    return '' + Math.floor(Date.now() / 1000);
  }

  generateSignature(
    identifier: string,
    orderData: any,
    signatureKey: string,
    signedElement: string | null
  ): string {
    const orderNumber = orderData.order.number;
    const amount = orderData.order.amount;
    const currencyCode = orderData.order.currency.code;
    const signatureBase = signedElement
      ? identifier + signedElement + signatureKey
      : identifier + orderNumber + currencyCode + amount + signatureKey;
    return typeof sha512 === 'function' ? sha512(signatureBase) : btoa(signatureBase);
  }

  selectMethod(id: string): void {
    this.selected = id;
    this.result = null;
    this.resultData = null;
    this.destroyForm();
    this.cdr.detectChanges();
    if (id === 'cards' || id === 'agora') {
      this.loadSdk().then(() => this.initForm());
    }
  }

  private initForm(): void {
    if (!this.epagoContainer?.nativeElement || typeof Epago === 'undefined') {
      return;
    }
    const container = this.epagoContainer.nativeElement;
    container.innerHTML = '';

    const orderNumber = this.generateOrderId();
    const isAgora = this.selected === 'agora';
    const orderMinimalData = {
      order: {
        number: orderNumber,
        amount: this.amount,
        fee: this.fee,
        taxes: 0,
        discounts: -(Math.sign(this.discounts) === 1 ? this.discounts : -this.discounts),
        shippingAmount: this.shipping,
        country: { code: 'PER' },
        currency: { code: this.currency },
        products: [...this.items],
        customer: {
          identifier: this.customer.identifier,
          name: this.customer.name,
          lastName: this.customer.lastName,
          address: {
            country: 'PER',
            levels: ['LIMA', 'LIMA', 'SAN ISIDRO'],
            line1: this.customer.address1 || 'Av Zeta 1',
            zip: '051014',
          },
          email: this.customer.email,
          phone: this.customer.phone,
          document: { type: 'DNI', number: this.customer.documentNumber },
        },
      },
      settings: {
        paymentMethods: isAgora ? ['AGORAWEB'] : ['tarjeta oh cuotas'],
        brands: isAgora ? ['AGORAWEB'] : ['VISA', 'MSCD', 'AMEX', 'DINC'],
        language: 'es_PE',
        businessService: 'WEB',
      },
    };

    const signedElementStr = orderNumber + this.currency + this.amount;
    const signature = this.generateSignature(
      this.apiKey.trim(),
      orderMinimalData,
      this.secret.trim(),
      null
    );

    const formGeneration = ++this.formGeneration;
    const config: any = {
      transaction: orderMinimalData,
      authentication: {
        identifier: this.apiKey.trim(),
        signature,
        signedElement: signedElementStr,
      },
      environment: this.currentSdkHost.environment,
      version: '2',
      hidePayButton: true,
      listeners: {
        afterPay: (response: any) => {
          console.log('afterPay', response);
          if (formGeneration !== this.formGeneration) return;
          this.formReady = false;
          this.resultData = response;
          this.payResult = this.readAfterPayResult(response);
          this.result = this.isAfterPayAccepted(response) ? 'success' : 'error';
          this.cdr.detectChanges();
        },
        afterClickOnPay: () => {},
        afterTimeExpired: () => {},
        response: () => {},
        showPaymentMethods: () => {},
        showChallenge: () => {},
        clearChallenge: () => {},
        doAction: () => {},
        doCloseAuthentication: () => {},
        doBackAction: () => {},
      },
    };
    if (this.typography !== 'default') {
      config.typography = this.typography;
    }

    try {
      const form = new Epago.Form(config);
      form.render('#epago-form-container');
      this.formRef = form;
      this.formReady = true;
      this.cdr.detectChanges();
    } catch (err) {
      console.error('Error inicializando SDK:', err);
      this.formReady = false;
      this.cdr.detectChanges();
    }
  }

  private isAfterPayAccepted(response: any): boolean {
    const legacyAccepted = response?.data?.result?.accepted === true;
    const processorAccepted = response?.data?.data?.processor?.result?.accepted === true;
    const code = response?.data?.data?.code || response?.data?.result?.code;
    return legacyAccepted || processorAccepted || code === 'AUTHORIZED';
  }

  private readAfterPayResult(response: any): {
    message: string;
    processorMessage: string;
    orderNumber: string;
    amountLabel: string;
    referenceNumber: string;
  } {
    const legacyResult = response?.data?.result;
    const authorize = response?.data?.data;
    const processor = authorize?.processor;
    const processorResult = processor?.result;
    const order = response?.data?.order || processor?.order;
    const currency = order?.currency?.code || this.currency;
    const amount = order?.amount ?? this.amount;
    return {
      message:
        processorResult?.message ||
        authorize?.message ||
        legacyResult?.message ||
        response?.data?.message?.text ||
        response?.message?.text ||
        '',
      processorMessage:
        processorResult?.processorResult?.message ||
        legacyResult?.processorResult?.message ||
        '',
      orderNumber: order?.number || '',
      amountLabel: amount != null && amount !== '' ? `${currency} ${amount}` : '',
      referenceNumber:
        processorResult?.processorResult?.referenceNumber ||
        legacyResult?.processorResult?.referenceNumber ||
        '',
    };
  }

  private destroyForm(): void {
    this.formGeneration++;
    this.formRef = null;
    this.formReady = false;
    const container = document.getElementById('epago-form-container');
    if (container) container.innerHTML = '';
  }

  handlePayClick(): void {
    if (this.selected !== 'cards' && this.selected !== 'agora') return;
    if (!this.formRef) return;
    try {
      this.formRef.submit();
    } catch (err) {
      console.error('Error al pagar:', err);
    }
  }

  handleBackFromResult(): void {
    window.location.reload();
  }

  onTypographyChange(): void {
    this.loadGoogleFont();
    this.remountFormIfNeeded();
  }
}
